import { CONFIG } from "../../appConfig";
import STATE from "../../constants/state";

const replacement = "replacement/";

export default {
  /**
   * @desc Initiates the replacement flow logic
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.prepStart();
   */
  prepStart() {
    return {
      endpoint: `${replacement}prep/start`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  },

  /**
   * @desc Search driver details that's entered on driver lookup page
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.searchByDriverLicense(data);
   */
  searchByDriverLicense(data) {
    return {
      endpoint: `${replacement}driver/searchByDriverLicense`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for confirming associated user
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.driverConfirm();
   */
  driverConfirm(data) {
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${replacement}driver/confirm`,
      data
    };
  },

  /**
   * @desc Endpoint for associating user to the ticket
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.driverAssociate();
   */
  driverAssociate() {
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${replacement}driver/associate`
    };
  },

  /**
   * @desc Calls an endpoint that will turn a driver update flag in BE side to true
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example services.gma.replacement.driverUpdate();
   */
  driverUpdate() {
    return {
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${replacement}driver/update`
    };
  },

  /**
   * @desc Endpoint for pickup date
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.pickupDate(data);
   */
  pickupDate(data = {}) {
    return {
      endpoint: `${replacement}pickupDate`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for pickup location
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.pickupLocation(data);
   */
  pickupLocation(data = {}) {
    return {
      endpoint: `${replacement}pickupLocation`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for custom location
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.navigateCustomLocation(data);
   */
  navigateCustomLocation(data = {}) {
    return {
      endpoint: `${replacement}navigate/customLocation`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for branch location
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.navigateBranchLocation(data);
   */
  navigateBranchLocation(data = {}) {
    return {
      endpoint: `${replacement}navigate/branchLocation`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for pickup location
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.navigatePickupLocation(data);
   *
   * // Payload example
   * data: {
   *  "pickup_delivery_address": {
   *    "country_code": "US",
   *     "city": "New York",
   *     "postal": "97060708",
   *     "street_addresses": [
   *         "Test Address"
   *     ],
   *     "country_subdivision_code": "NY"
   *  }
   * }
   *
   */
  navigatePickupLocation(data = {}) {
    return {
      endpoint: `${replacement}navigate/pickupLocation`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for follow up
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.navigateFollowUp(data);
   */
  navigateFollowUp(data = {}) {
    return {
      endpoint: `${replacement}navigate/followUp`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for submitting location
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.locationSubmit(data);
   */
  locationSubmit(data = {}) {
    return {
      endpoint: `${replacement}location/submit`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for confirming follow up date
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.followUpDateConfirm(data);
   */
  followUpDateConfirm(data = {}) {
    return {
      endpoint: `${replacement}followUpDate/confirm`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for getting available vehicles
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.availableVehicles();
   */
  availableVehicles() {
    return {
      endpoint: `${replacement}availableVehicles`,
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      statePath: STATE.GMA.REPLACEMENT.AVAILABLE_VEHICLES
    };
  },

  /**
   * @desc Endpoint for selecting car class
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.selectCarClass(data);
   */
  selectCarClass(data = {}) {
    return {
      endpoint: `${replacement}selectCarClass`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for selecting class details by car class code
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * const data = {
   *  carClassCode: *(String)* i.e. "ECAR",
   * }
   *
   * services.gma.replacement.carClassCode(data);
   * // dispatched results stored in `state.gmi.gma.replacement.car_class_details[carClassCode]
   */
  carClassCode(data = {}) {
    const { carClassCode } = data;
    const statePath = STATE.GMA.REPLACEMENT.CAR_CLASS_DETAILS.concat(carClassCode);
    return {
      endpoint: `${replacement}carClass/${carClassCode}`,
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      data,
      statePath
    };
  },

  /**
   * @desc Endpoint for commiting prep
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.prepCommit(data);
   */
  prepCommit() {
    return {
      endpoint: `${replacement}prep/commit`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  },

  /**
   * @desc Endpoint for modifying initiate
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.modifyInitiate();
   */
  modifyInitiate() {
    return {
      endpoint: `${replacement}modify/initiate`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  },

  /**
   * @desc Endpoint for navigating to pickupLocation
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.checkinStart();
   */
  checkinStart() {
    return {
      endpoint: `${replacement}checkIn/start`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  },

  /**
   * @desc Endpoint for protections
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.protections();
   */
  protections(data = {}) {
    return {
      endpoint: `${replacement}protections`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for extras
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.extras();
   */
  extras(data = {}) {
    return {
      endpoint: `${replacement}extras`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for initiating payment
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.paymentInitiate();
   */
  paymentInitiate(data = {}) {
    return {
      endpoint: `${replacement}payment/initiate`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data,
      statePath: STATE.GMA.REPLACEMENT.PAYMENT.INITIATE.PATH
    };
  },

  /**
   * @desc Endpoint for recording payment transaction
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.paymentRecordPaymentTransaction();
   */
  paymentRecordPaymentTransaction(data = {}) {
    return {
      endpoint: `${replacement}payment/recordPaymentTransaction`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for payment confirm
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.paymentConfirm();
   */
  paymentConfirm(data = {}) {
    return {
      endpoint: `${replacement}payment/confirm`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc Endpoint for getting terms and conditions
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.termsAndConditions();
   */
  termsAndConditions() {
    return {
      endpoint: `${replacement}termsAndConditions`,
      method: "GET",
      origin: CONFIG.GMA_ORIGIN_URL,
      statePath: STATE.GMA.REPLACEMENT.TERMS_AND_CONDITIONS
    };
  },

  /**
   * @desc Endpoint for commiting check-in
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.checkInCommit();
   */
  checkInCommit(data = {}) {
    return {
      endpoint: `${replacement}checkIn/commit`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL,
      data
    };
  },

  /**
   * @desc endpoint used to upgrade car class
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.upgradeCarClass();
   */
  upgradeCarClass() {
    return {
      endpoint: `${replacement}upgradeCarClass`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  },

  /**
   * @desc Endpoint for navigate to car select page
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.navigateCarSelect();
   */
  navigateCarSelect() {
    return {
      endpoint: `${replacement}navigate/carSelect`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  },

  /**
   * @desc Endpoint to navigate to date time page
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.navigateDateTime();
   */
  navigateDateTime() {
    return {
      endpoint: `${replacement}navigate/dateTime`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  },

  /**
   * @desc Endpoint to navigate to the flow dashboard
   * @returns {Object}
   * @memberof services.gma.replacement
   * @example
   * services.gma.replacement.navigateDashboard();
   */
  navigateDashboard() {
    return {
      endpoint: `${replacement}navigate/dashboard`,
      method: "POST",
      origin: CONFIG.GMA_ORIGIN_URL
    };
  }
};
